import React, { useCallback, useEffect } from 'react';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { useAuth } from '../context/AuthContext';
import { useSafeTwilioConversations } from '../contexts/TwilioConversationsContext';
import { FeatureFlag, useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { getUnreadCount } from '../services/notificationService';
import { clearAppBadgeCount, setAppBadgeCount } from '../services/appBadgeService';
import { computeAppBadgeCount } from '../utils/appBadgeCount';

const PUSH_RECEIVED = 'native-push-received';
const POLL_MS = 30_000;

/**
 * Synchronise la pastille d'icône OS avec notifs + unread Conversations (app Pro).
 * À monter **à l'intérieur** de TwilioConversationsProvider.
 */
export function useSyncAppIconBadge(): void {
  const { isAuthenticated } = useAuth();
  const { conversations } = useSafeTwilioConversations();
  const { isFeatureEnabled } = useFeatureFlags();
  const chatOn = isFeatureEnabled(FeatureFlag.CHAT_FEATURE);

  const sync = useCallback(async () => {
    if (!isAuthenticated) {
      await clearAppBadgeCount();
      return;
    }

    const notif = await getUnreadCount();
    const total = computeAppBadgeCount(notif, conversations, {
      includeChat: chatOn,
    });
    await setAppBadgeCount(total);
  }, [chatOn, conversations, isAuthenticated]);

  useEffect(() => {
    void sync();
  }, [sync]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const id = window.setInterval(() => void sync(), POLL_MS);
    const onPush = () => {
      void sync();
    };
    window.addEventListener(PUSH_RECEIVED, onPush);
    return () => {
      window.clearInterval(id);
      window.removeEventListener(PUSH_RECEIVED, onPush);
    };
  }, [isAuthenticated, sync]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let handle: PluginListenerHandle | null = null;
    let cancelled = false;

    void App.addListener('appStateChange', (state) => {
      if (!cancelled && state.isActive) {
        void sync();
      }
    }).then((listener) => {
      if (cancelled) {
        void listener.remove();
      } else {
        handle = listener;
      }
    });

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, [sync]);
}

/** Composant no-UI pour brancher le hook dans le layout. */
export const AppIconBadgeSync: React.FC = () => {
  useSyncAppIconBadge();
  return null;
};
